// ============================================================================
// DATA CONSTANTS - Matching the PDF exactly
// ============================================================================

import { FileText, Users, Shield, Scale, Lock, Eye } from 'lucide-react';

export const GOVERNANCE_ITEMS = [
  {
    id: 'ab869',
    name: 'AB 869 Compliance',
    type: 'regulation',
    icon: Scale,
    summary: 'California Assembly Bill 869 - State Agency Information Security Requirements',
    description: 'AB 869 mandates that California state agencies implement specific information security controls, including data classification, audit logging, and retention policies. This regulation directly impacts Zero Trust implementation requirements.',
    requirements: [
      'Data classification for all sensitive information',
      'Audit logging with minimum 3-year retention',
      'Annual security assessments',
      'Incident response procedures',
      'Access control based on least privilege'
    ],
    links: [
      { label: 'AB 869 Full Text', url: 'https://leginfo.legislature.ca.gov/faces/billNavClient.xhtml?bill_id=202320240AB869' },
      { label: 'CDT Security Guidelines', url: 'https://cdt.ca.gov/security/' },
      { label: 'SIMM 5300 Series', url: 'https://cdt.ca.gov/policy/simm/' }
    ],
    complianceFields: ['lastAssessmentDate', 'nextAssessmentDue', 'complianceScore', 'findings']
  },
  {
    id: 'zt-steering',
    name: 'Zero Trust Steering Committee',
    type: 'governance-body',
    icon: Users,
    summary: 'Executive committee overseeing Zero Trust strategy and implementation',
    description: 'The Zero Trust Steering Committee provides executive oversight for the organization\'s Zero Trust transformation. This body sets strategic direction, approves funding, resolves cross-functional conflicts, and tracks progress against maturity goals.',
    requirements: [
      'Monthly steering committee meetings',
      'Quarterly progress reports to executive leadership',
      'Annual strategy review and roadmap updates',
      'Budget approval for ZT initiatives',
      'Escalation path for blockers'
    ],
    links: [
      { label: 'CISA Zero Trust Maturity Model', url: 'https://www.cisa.gov/zero-trust-maturity-model' },
      { label: 'NIST SP 800-207', url: 'https://csrc.nist.gov/publications/detail/sp/800-207/final' }
    ],
    complianceFields: ['chairPerson', 'meetingCadence', 'lastMeetingDate', 'members']
  },
  {
    id: 'data-governance',
    name: 'Data Governance Council',
    type: 'governance-body',
    icon: FileText,
    summary: 'Cross-functional body responsible for data policies and standards',
    description: 'The Data Governance Council establishes and enforces policies for data classification, handling, retention, and access. This council is critical for Zero Trust as data-centric security requires clear ownership and classification of all data assets.',
    requirements: [
      'Data classification policy and taxonomy',
      'Data ownership assignment for all critical data',
      'Data access request and approval workflow',
      'Data retention and disposal schedules',
      'Privacy impact assessments'
    ],
    links: [
      { label: 'NIST Data Classification', url: 'https://csrc.nist.gov/glossary/term/data_classification' },
      { label: 'California CCPA', url: 'https://oag.ca.gov/privacy/ccpa' }
    ],
    complianceFields: ['dataOwners', 'classificationComplete', 'lastPolicyReview']
  },
  {
    id: 'arb',
    name: 'Architecture Review Board',
    type: 'governance-body',
    icon: Shield,
    summary: 'Technical review body ensuring ZT alignment in all projects',
    description: 'The Architecture Review Board (ARB) ensures all technology projects align with Zero Trust principles. Every new application, infrastructure change, or vendor integration must pass ARB review to verify ZT compliance before deployment.',
    requirements: [
      'ZT compliance checklist for all projects',
      'Mandatory ARB review for production deployments',
      'Exception process for legacy systems',
      'Vendor security assessment requirements',
      'Integration pattern standards'
    ],
    links: [
      { label: 'Zero Trust Architecture Principles', url: 'https://www.nist.gov/publications/zero-trust-architecture' }
    ],
    complianceFields: ['reviewBacklog', 'exceptionsGranted', 'lastReviewDate']
  },
  {
    id: 'identity-first',
    name: 'Identity-First Access Policy',
    type: 'policy',
    icon: Lock,
    summary: 'All access decisions start with verified identity',
    description: 'Identity-First Access mandates that every access request must begin with strong identity verification. No network location, IP address, or device alone grants access - identity is the primary control plane for all resource access.',
    requirements: [
      'MFA required for all users',
      'No implicit trust based on network location',
      'Identity verification before every access decision',
      'Service accounts with managed identities',
      'Phishing-resistant authentication (FIDO2/WebAuthn)'
    ],
    links: [
      { label: 'NIST Digital Identity Guidelines', url: 'https://pages.nist.gov/800-63-3/' },
      { label: 'FIDO Alliance', url: 'https://fidoalliance.org/' }
    ],
    complianceFields: ['mfaEnrollment', 'phishingResistantPct', 'serviceAccountsManaged']
  },
  {
    id: 'least-privilege',
    name: 'Least Privilege Enforcement',
    type: 'policy',
    icon: Lock,
    summary: 'Users receive minimum access necessary for their role',
    description: 'Least Privilege Enforcement ensures users, services, and applications have only the minimum permissions required to perform their functions. Access is granted just-in-time, scoped narrowly, and automatically revoked when no longer needed.',
    requirements: [
      'Role-based access control (RBAC) implementation',
      'Just-in-time (JIT) privileged access',
      'Quarterly access reviews',
      'Automatic deprovisioning on role change',
      'No standing privileged access'
    ],
    links: [
      { label: 'NIST Least Privilege', url: 'https://csrc.nist.gov/glossary/term/least_privilege' },
      { label: 'PAM Best Practices', url: 'https://www.gartner.com/en/documents/3991496' }
    ],
    complianceFields: ['lastAccessReview', 'orphanedAccounts', 'jitEnabled']
  },
  {
    id: 'data-classification',
    name: 'Data Classification Policy',
    type: 'policy',
    icon: FileText,
    summary: 'All data must be classified and labeled by sensitivity',
    description: 'Data Classification Policy requires all data to be categorized by sensitivity level (Public, Internal, Confidential, Restricted). Classification drives encryption requirements, access controls, retention, and handling procedures.',
    requirements: [
      'Four-tier classification taxonomy',
      'Automated classification tools deployed',
      'Labels applied to all new data',
      'Legacy data classification project',
      'DLP policies enforcing classification'
    ],
    links: [
      { label: 'Microsoft Purview Classification', url: 'https://learn.microsoft.com/en-us/microsoft-365/compliance/data-classification-overview' },
      { label: 'Data Classification Standards', url: 'https://www.iso.org/standard/75652.html' }
    ],
    complianceFields: ['classifiedDataPct', 'dlpPoliciesActive', 'classificationTool']
  },
  {
    id: 'continuous-verification',
    name: 'Continuous Verification Policy',
    type: 'policy',
    icon: Eye,
    summary: 'Trust is continuously evaluated, never assumed',
    description: 'Continuous Verification means trust is never implicit or permanent. Every session, transaction, and access request is evaluated against current risk signals including device health, user behavior, threat intelligence, and data sensitivity.',
    requirements: [
      'Real-time risk signal integration',
      'Session re-evaluation on risk change',
      'Adaptive access policies',
      'Behavioral analytics (UEBA)',
      'Step-up authentication for high-risk actions'
    ],
    links: [
      { label: 'Continuous Access Evaluation', url: 'https://learn.microsoft.com/en-us/azure/active-directory/conditional-access/concept-continuous-access-evaluation' }
    ],
    complianceFields: ['riskSignalsIntegrated', 'uebaDeployed', 'adaptivePolicies']
  },
  {
    id: 'cisa-maturity',
    name: 'CISA ZT Maturity Model',
    type: 'framework',
    icon: Shield,
    summary: 'CISA\'s framework for measuring Zero Trust maturity',
    description: 'The CISA Zero Trust Maturity Model provides a roadmap for federal agencies (and adoptable by state/local) to achieve Zero Trust. It defines maturity across five pillars: Identity, Device, Network, Application, and Data - from Traditional through Optimal.',
    requirements: [
      'Annual maturity assessment',
      'Pillar-by-pillar improvement plans',
      'Target maturity level per pillar',
      'Gap analysis and remediation tracking',
      'Progress reporting to leadership'
    ],
    links: [
      { label: 'CISA Zero Trust Maturity Model v2.0', url: 'https://www.cisa.gov/zero-trust-maturity-model' },
      { label: 'CISA ZT Resources', url: 'https://www.cisa.gov/topics/cybersecurity-best-practices/zero-trust' },
      { label: 'OMB M-22-09 Federal ZT Strategy', url: 'https://www.whitehouse.gov/wp-content/uploads/2022/01/M-22-09.pdf' }
    ],
    complianceFields: ['currentMaturityLevel', 'targetMaturityLevel', 'lastAssessmentDate']
  }
];

export const POLICY_SIGNALS = [
  {
    id: 'identity',
    label: 'IDENTITY SIGNAL',
    question: 'Who is this user?',
    defaultVendors: 'Okta / Azure AD / Ping',
    description: 'The Identity Signal verifies who is requesting access. This is the foundational signal in Zero Trust - every access decision starts with confirmed identity.',
    vendorOptions: [
      { vendor: 'Okta', products: ['Okta Identity Cloud', 'Okta Workforce Identity', 'Okta Customer Identity', 'Okta Adaptive MFA'] },
      { vendor: 'Microsoft', products: ['Azure Active Directory', 'Azure AD B2C', 'Microsoft Entra ID', 'Azure AD Premium P2'] },
      { vendor: 'Google', products: ['Google Cloud Identity', 'Google Workspace Identity', 'Google Cloud IAM', 'Google Cloud Identity Platform'] },
      { vendor: 'Ping Identity', products: ['PingOne', 'PingFederate', 'PingID', 'PingAccess'] },
      { vendor: 'OneLogin', products: ['OneLogin Workforce Identity', 'OneLogin Access', 'OneLogin MFA'] },
      { vendor: 'ForgeRock', products: ['ForgeRock Identity Platform', 'ForgeRock Access Management', 'ForgeRock Identity Gateway'] },
      { vendor: 'CyberArk', products: ['CyberArk Identity', 'CyberArk Workforce Identity', 'CyberArk SSO'] },
      { vendor: 'SailPoint', products: ['SailPoint IdentityNow', 'SailPoint IdentityIQ', 'SailPoint Access Risk Management'] },
      { vendor: 'IBM', products: ['IBM Security Verify', 'IBM Security Access Manager'] },
      { vendor: 'RSA', products: ['RSA SecurID', 'RSA ID Plus', 'RSA Governance & Lifecycle'] },
      { vendor: 'Auth0', products: ['Auth0 Platform', 'Auth0 Universal Login', 'Auth0 MFA'] }
    ],
    assessmentQuestions: [
      { id: 'collected', question: 'Is this signal being collected?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'coverage', question: 'What percentage of users are covered?', type: 'percentage' },
      { id: 'mfa', question: 'Is MFA enforced for all identity verifications?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'realtime', question: 'Is identity verification real-time?', type: 'choice', options: ['Yes', 'No'] }
    ],
    integrationChecks: ['SSO configured', 'MFA enabled', 'API connected', 'Logs flowing to SIEM']
  },
  {
    id: 'device',
    label: 'DEVICE SIGNAL',
    question: 'Is device trusted?',
    defaultVendors: 'CrowdStrike / InTune',
    description: 'The Device Signal assesses whether the device requesting access meets security requirements - is it managed, compliant, healthy, and free of threats?',
    vendorOptions: [
      { vendor: 'CrowdStrike', products: ['Falcon Endpoint Protection', 'Falcon Insight XDR', 'Falcon Device Control', 'Falcon Zero Trust Assessment'] },
      { vendor: 'Microsoft', products: ['Microsoft Intune', 'Microsoft Defender for Endpoint', 'Azure AD Device Registration', 'Endpoint Manager'] },
      { vendor: 'Google', products: ['Google Endpoint Management', 'Chrome Enterprise', 'BeyondCorp Enterprise', 'Google Workspace MDM'] },
      { vendor: 'VMware', products: ['Workspace ONE UEM', 'Workspace ONE Intelligence', 'Carbon Black Cloud'] },
      { vendor: 'Jamf', products: ['Jamf Pro', 'Jamf Protect', 'Jamf Connect', 'Jamf Trust'] },
      { vendor: 'SentinelOne', products: ['Singularity Platform', 'Singularity XDR', 'Singularity Mobile'] },
      { vendor: 'Tanium', products: ['Tanium Core Platform', 'Tanium Comply', 'Tanium Protect', 'Tanium Endpoint Management'] },
      { vendor: 'Ivanti', products: ['Ivanti Neurons for MDM', 'Ivanti Endpoint Manager', 'Ivanti Neurons for UEM'] },
      { vendor: 'Cisco', products: ['Cisco Secure Endpoint', 'Cisco Meraki MDM', 'Cisco Duo Device Trust'] },
      { vendor: 'Sophos', products: ['Sophos Central', 'Sophos Mobile', 'Sophos Intercept X'] },
      { vendor: 'Palo Alto', products: ['Cortex XDR', 'GlobalProtect', 'Prisma Access for Mobile'] }
    ],
    assessmentQuestions: [
      { id: 'collected', question: 'Is this signal being collected?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'coverage', question: 'What percentage of devices are enrolled?', type: 'percentage' },
      { id: 'posture', question: 'Is device posture checked before access?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'realtime', question: 'Is device health monitored continuously?', type: 'choice', options: ['Yes', 'No'] }
    ],
    integrationChecks: ['MDM/UEM enrolled', 'EDR agent deployed', 'Compliance policies active', 'Certificate-based identity']
  },
  {
    id: 'risk',
    label: 'RISK SIGNAL',
    question: 'Behavior + Threat Intel',
    defaultVendors: 'Microsoft Sentinel / Splunk',
    description: 'The Risk Signal combines behavioral analytics (UEBA), threat intelligence feeds, and anomaly detection to assess the risk level of each access request.',
    vendorOptions: [
      { vendor: 'Google', products: ['Google Chronicle SIEM', 'Google Chronicle SOAR', 'Google Security Command Center', 'Google Cloud Threat Intelligence'] },
      { vendor: 'Microsoft', products: ['Microsoft Sentinel', 'Microsoft Defender XDR', 'Azure AD Identity Protection', 'Microsoft Defender for Identity'] },
      { vendor: 'Splunk', products: ['Splunk Enterprise Security', 'Splunk SOAR', 'Splunk UBA', 'Splunk Attack Analyzer'] },
      { vendor: 'Exabeam', products: ['Exabeam Fusion', 'Exabeam SIEM', 'Exabeam UEBA', 'Exabeam Security Analytics'] },
      { vendor: 'Securonix', products: ['Securonix SIEM', 'Securonix UEBA', 'Securonix SOAR', 'Securonix Threat Intelligence'] },
      { vendor: 'IBM', products: ['IBM QRadar SIEM', 'IBM QRadar SOAR', 'IBM QRadar UBA', 'IBM X-Force Threat Intelligence'] },
      { vendor: 'Palo Alto', products: ['Cortex XSIAM', 'Cortex XSOAR', 'Unit 42 Threat Intelligence', 'Cortex XDR Analytics'] },
      { vendor: 'CrowdStrike', products: ['Falcon Intelligence', 'Falcon OverWatch', 'Falcon LogScale', 'Falcon Identity Threat Detection'] },
      { vendor: 'Rapid7', products: ['InsightIDR', 'InsightConnect', 'Threat Command'] },
      { vendor: 'LogRhythm', products: ['LogRhythm SIEM', 'LogRhythm NDR', 'LogRhythm UEBA'] },
      { vendor: 'Sumo Logic', products: ['Sumo Logic Cloud SIEM', 'Cloud SOAR', 'Cloud Security Monitoring'] }
    ],
    assessmentQuestions: [
      { id: 'collected', question: 'Is this signal being collected?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'ueba', question: 'Is User Behavior Analytics (UEBA) deployed?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'threatintel', question: 'Are threat intelligence feeds integrated?', type: 'choice', options: ['Yes', 'No'] },
      { id: 'alerting', question: 'Are risk-based alerts configured?', type: 'choice', options: ['Yes', 'No'] }
    ],
    integrationChecks: ['SIEM connected', 'UEBA rules active', 'Threat feeds subscribed', 'Risk scores calculated']
  },
  {
    id: 'data',
    label: 'DATA CLASSIFICATION',
    question: 'What data is at stake?',
    defaultVendors: 'Microsoft Purview / Varonis',
    description: 'The Data Classification Signal determines the sensitivity of the data being accessed, enabling appropriate access controls based on data value and risk.',
    vendorOptions: [
      { vendor: 'Microsoft', products: ['Microsoft Purview', 'Microsoft Information Protection', 'Azure Information Protection', 'Microsoft Compliance Manager'] },
      { vendor: 'Google', products: ['Google Cloud DLP', 'Google Cloud Data Loss Prevention', 'Google Sensitive Data Protection', 'Google Data Catalog'] },
      { vendor: 'Varonis', products: ['Varonis Data Security Platform', 'Varonis DatAdvantage', 'Varonis Data Classification Engine', 'Varonis DatAlert'] },
      { vendor: 'Forcepoint', products: ['Forcepoint DLP', 'Forcepoint Data Discovery', 'Forcepoint CASB'] },
      { vendor: 'Symantec/Broadcom', products: ['Symantec DLP', 'Symantec Information Centric Analytics', 'CloudSOC CASB'] },
      { vendor: 'Digital Guardian', products: ['Digital Guardian DLP', 'Digital Guardian Analytics & Reporting Cloud'] },
      { vendor: 'Trellix', products: ['Trellix DLP', 'Trellix Data Security'] },
      { vendor: 'Netskope', products: ['Netskope DLP', 'Netskope CASB', 'Netskope Data Protection'] },
      { vendor: 'Proofpoint', products: ['Proofpoint Enterprise DLP', 'Proofpoint Information Protection'] },
      { vendor: 'PKWARE', products: ['PKWARE Data Discovery', 'PKWARE Data Classification', 'PKWARE Encryption'] },
      { vendor: 'BigID', products: ['BigID Data Intelligence', 'BigID Data Discovery', 'BigID Data Classification'] }
    ],
    assessmentQuestions: [
      { id: 'collected', question: 'Is this signal being collected?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'classified', question: 'What percentage of data is classified?', type: 'percentage' },
      { id: 'labels', question: 'Are sensitivity labels applied automatically?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'dlp', question: 'Is DLP enforcing classification policies?', type: 'choice', options: ['Yes', 'Partial', 'No'] }
    ],
    integrationChecks: ['Classification tool deployed', 'Labels defined', 'DLP policies active', 'Data discovery complete']
  },
  {
    id: 'context',
    label: 'CONTEXT SIGNAL',
    question: 'Location / Time / Pattern',
    defaultVendors: 'Cisco Duo / Zscaler',
    description: 'The Context Signal evaluates environmental factors - where is the request coming from, what time is it, does it match normal patterns for this user?',
    vendorOptions: [
      { vendor: 'Google', products: ['BeyondCorp Enterprise', 'Google Cloud IAP', 'Google Cloud Armor', 'Google VPC Service Controls'] },
      { vendor: 'Cisco', products: ['Cisco Duo', 'Cisco Umbrella', 'Cisco Secure Access', 'Cisco ISE'] },
      { vendor: 'Zscaler', products: ['Zscaler Internet Access', 'Zscaler Private Access', 'Zscaler Digital Experience'] },
      { vendor: 'Palo Alto', products: ['Prisma Access', 'GlobalProtect', 'Prisma SASE'] },
      { vendor: 'Netskope', products: ['Netskope SASE', 'Netskope Private Access', 'Netskope SWG'] },
      { vendor: 'Cloudflare', products: ['Cloudflare Access', 'Cloudflare Gateway', 'Cloudflare Zero Trust'] },
      { vendor: 'Akamai', products: ['Akamai Enterprise Application Access', 'Akamai MFA', 'Akamai Guardicore'] },
      { vendor: 'Fortinet', products: ['FortiSASE', 'FortiGate ZTNA', 'FortiClient'] },
      { vendor: 'Absolute', products: ['Absolute Secure Access', 'Absolute Resilience', 'Absolute Visibility'] },
      { vendor: 'Perimeter 81', products: ['Perimeter 81 ZTNA', 'Perimeter 81 SWG', 'Perimeter 81 SASE'] },
      { vendor: 'Cato Networks', products: ['Cato SASE Cloud', 'Cato SDP', 'Cato DEM'] }
    ],
    assessmentQuestions: [
      { id: 'collected', question: 'Is this signal being collected?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
      { id: 'location', question: 'Is geolocation verified?', type: 'choice', options: ['Yes', 'No'] },
      { id: 'time', question: 'Are time-based access policies enforced?', type: 'choice', options: ['Yes', 'No'] },
      { id: 'adaptive', question: 'Are adaptive/conditional policies in place?', type: 'choice', options: ['Yes', 'Partial', 'No'] }
    ],
    integrationChecks: ['Geolocation enabled', 'Time policies configured', 'Impossible travel detection', 'Network context captured']
  }
];

// Policy Decision Point Configuration
export const PDP_CONFIG = {
  id: 'pdp',
  label: 'POLICY DECISION POINT (PDP)',
  subtitle: 'Continuous Trust Evaluation Engine',
  description: 'The Policy Decision Point is the central brain of Zero Trust architecture. It receives signals from all sources (Identity, Device, Risk, Data, Context), evaluates them against defined policies, and makes real-time access decisions.',
  vendorOptions: [
    { vendor: 'Microsoft', products: ['Azure AD Conditional Access', 'Microsoft Entra ID', 'Microsoft Entra Workload ID', 'Azure AD Identity Protection'] },
    { vendor: 'Okta', products: ['Okta Identity Engine', 'Okta Adaptive MFA', 'Okta FastPass', 'Okta Policy Engine'] },
    { vendor: 'Google', products: ['BeyondCorp Enterprise', 'Google Cloud IAP', 'Access Context Manager', 'Google Cloud Policy Engine'] },
    { vendor: 'Ping Identity', products: ['PingAccess', 'PingAuthorize', 'PingOne Authorize', 'PingFederate Policy Engine'] },
    { vendor: 'Zscaler', products: ['Zscaler Policy Engine', 'Zscaler Private Access Policy', 'Zscaler Zero Trust Exchange'] },
    { vendor: 'Cisco', products: ['Cisco Duo Policy Engine', 'Cisco ISE', 'Cisco Secure Access Policy'] },
    { vendor: 'Palo Alto', products: ['Prisma Access Policy Engine', 'Cortex XSOAR Policies', 'GlobalProtect Policy'] },
    { vendor: 'CrowdStrike', products: ['Falcon Zero Trust Assessment', 'Falcon Identity Protection Policies'] },
    { vendor: 'Cloudflare', products: ['Cloudflare Access Policies', 'Cloudflare Zero Trust Policy Engine'] },
    { vendor: 'Netskope', products: ['Netskope Policy Engine', 'Netskope Zero Trust Engine', 'Netskope Adaptive Access'] }
  ],
  signalIntegrations: [
    { id: 'identity', label: 'Identity Signal', description: 'User identity verification (MFA, SSO)' },
    { id: 'device', label: 'Device Signal', description: 'Device posture and compliance' },
    { id: 'risk', label: 'Risk Signal', description: 'UEBA and threat intelligence' },
    { id: 'data', label: 'Data Signal', description: 'Data classification and sensitivity' },
    { id: 'context', label: 'Context Signal', description: 'Location, time, and behavior patterns' }
  ],
  decisionTypes: [
    { id: 'allow', label: 'Allow', description: 'Grant access with no additional steps' },
    { id: 'deny', label: 'Deny', description: 'Block access completely' },
    { id: 'stepup', label: 'Step-Up Auth', description: 'Require additional authentication' },
    { id: 'challenge', label: 'Challenge MFA', description: 'Prompt for MFA verification' },
    { id: 'limit', label: 'Limited Access', description: 'Grant read-only or restricted access' },
    { id: 'monitor', label: 'Allow + Monitor', description: 'Grant access with enhanced logging' }
  ],
  assessmentQuestions: [
    { id: 'deployed', question: 'Is a centralized PDP deployed?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
    { id: 'coverage', question: 'What percentage of access requests go through the PDP?', type: 'percentage' },
    { id: 'realtime', question: 'Does the PDP evaluate continuously (not just at login)?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
    { id: 'policies', question: 'Are policies centrally managed and version controlled?', type: 'choice', options: ['Yes', 'Partial', 'No'] },
    { id: 'logging', question: 'Are all access decisions logged for audit?', type: 'choice', options: ['Yes', 'Partial', 'No'] }
  ],
  integrationChecks: [
    'PDP receives Identity signals',
    'PDP receives Device posture',
    'PDP receives Risk/UEBA scores',
    'PDP aware of Data classification',
    'PDP evaluates Context (location/time)',
    'Decisions logged to SIEM',
    'Step-up authentication configured',
    'Deny policies enforced'
  ]
};

export const PILLARS = [
  {
    id: 'identity',
    name: 'IDENTITY',
    color: '#9333ea',
    capabilities: [
      { id: 'idp', name: 'IdP', vendors: 'Okta / Azure AD / Ping' },
      { id: 'mfa', name: 'MFA – Phishing-Resistant FIDO2', vendors: '' },
      { id: 'iga', name: 'Identity Governance & Admin (IGA)', vendors: '' },
      { id: 'pam', name: 'Privileged Access Mgmt (PAM)', vendors: '' },
      { id: 'machine-id', name: 'Machine / Service Identity', vendors: '' },
      { id: 'conditional', name: 'Risk-Based Conditional Access', vendors: '' }
    ]
  },
  {
    id: 'device',
    name: 'DEVICE',
    color: '#3b82f6',
    capabilities: [
      { id: 'edr', name: 'EDR / XDR', vendors: 'CrowdStrike / SentinelOne' },
      { id: 'uem', name: 'UEM / MDM', vendors: 'Intune / Jamf' },
      { id: 'posture', name: 'Device Posture Assessment', vendors: '' },
      { id: 'cert-id', name: 'Certificate-Based Device Identity', vendors: '' },
      { id: 'byod', name: 'BYOD Isolation / Containerization', vendors: '' },
      { id: 'patch', name: 'Patch & Vulnerability Mgmt', vendors: '' }
    ]
  },
  {
    id: 'network',
    name: 'NETWORK',
    color: '#06b6d4',
    capabilities: [
      { id: 'ztna', name: 'ZTNA – Replace VPN', vendors: 'Zscaler / Cisco' },
      { id: 'microseg', name: 'Microsegmentation', vendors: 'Illumio / Guardicore' },
      { id: 'dns', name: 'DNS Security', vendors: 'Cisco Umbrella' },
      { id: 'sdwan', name: 'SD-WAN / SASE', vendors: '' },
      { id: 'nta', name: 'Network Traffic Analysis (NTA)', vendors: '' },
      { id: 'lateral', name: 'East-West Lateral Movement Controls', vendors: '' }
    ]
  },
  {
    id: 'application',
    name: 'APPLICATION',
    color: '#10b981',
    capabilities: [
      { id: 'proxy', name: 'App Access Proxy', vendors: 'Cloudflare / Netskope' },
      { id: 'api', name: 'API Gateway & Security', vendors: 'Kong / Apigee' },
      { id: 'workload', name: 'Workload / Service Identity', vendors: '' },
      { id: 'mesh', name: 'Service Mesh', vendors: 'Istio / Linkerd' },
      { id: 'dast', name: 'DAST / SAST / RASP', vendors: '' },
      { id: 'secrets', name: 'Secrets Management', vendors: 'Vault' }
    ]
  },
  {
    id: 'data',
    name: 'DATA',
    color: '#f59e0b',
    ab869Critical: true,
    capabilities: [
      { id: 'classification', name: 'Data Classification', vendors: 'Purview / Varonis' },
      { id: 'dlp', name: 'DLP', vendors: 'Forcepoint / Symantec' },
      { id: 'encryption', name: 'Encryption (at-rest + in-transit + in-use)', vendors: '' },
      { id: 'access-gov', name: 'Data Access Governance', vendors: '' },
      { id: 'tokenization', name: 'Tokenization / Data Masking', vendors: '' },
      { id: 'ab869-audit', name: 'AB 869: Audit + Retention Controls', vendors: '', critical: true }
    ]
  }
  // Note: Visibility & Analytics is NOT a pillar - it's a cross-cutting function
  // displayed separately below the 5 CISA pillars
];

// Maturity level descriptions for each capability
// Based on CISA Zero Trust Maturity Model
export const CAPABILITY_MATURITY_DESCRIPTIONS = {
  // IDENTITY PILLAR
  'idp': {
    description: 'Identity Provider (IdP) centralizes user authentication and manages identity across enterprise systems.',
    traditional: 'Password-only authentication with local accounts. No centralized identity provider. Users have separate credentials for each application.',
    initial: 'Basic SSO implemented for some applications. Centralized IdP deployed but not all apps integrated. Password policies enforced.',
    advanced: 'Enterprise-wide SSO with most applications federated. Passwordless options available. Identity proofing for onboarding.',
    optimal: 'Unified identity fabric across cloud and on-prem. All apps federated. Continuous identity verification. Decentralized identity support.'
  },
  'mfa': {
    description: 'Multi-Factor Authentication requires multiple verification methods, with phishing-resistant FIDO2 being the gold standard.',
    traditional: 'Single-factor (password only) for most users. SMS/email OTP for sensitive systems. No phishing resistance.',
    initial: 'MFA required for privileged users and VPN. Mix of push notifications and OTP. Some phishing-resistant tokens for admins.',
    advanced: 'MFA for all users across all applications. FIDO2/WebAuthn deployed. Phishing-resistant required for privileged access.',
    optimal: 'Passwordless authentication default. All MFA is phishing-resistant. Continuous authentication based on risk signals.'
  },
  'iga': {
    description: 'Identity Governance & Administration manages the full identity lifecycle: joiner, mover, leaver processes.',
    traditional: 'Manual provisioning via tickets. Quarterly access reviews via spreadsheets. No automated deprovisioning.',
    initial: 'Basic HR-driven provisioning. Annual access reviews. Some automated deprovisioning for terminations.',
    advanced: 'Automated joiner/mover/leaver workflows. Role-based access control. Quarterly certifications with workflow.',
    optimal: 'AI-driven access recommendations. Continuous access reviews. Just-in-time provisioning. Birthright access automated.'
  },
  'pam': {
    description: 'Privileged Access Management secures elevated access to critical systems and infrastructure.',
    traditional: 'Shared admin accounts. Static passwords stored in documents. No session recording.',
    initial: 'Password vault for shared accounts. Basic check-out workflows. Some session logging.',
    advanced: 'Just-in-time privileged access. Session recording and monitoring. Privileged behavior analytics.',
    optimal: 'Zero standing privileges. All privileged access is JIT with approval. Real-time session analysis with auto-termination.'
  },
  'machine-id': {
    description: 'Machine/Service Identity ensures non-human identities (APIs, services, workloads) are authenticated and managed.',
    traditional: 'Hard-coded credentials in code. Long-lived API keys. No certificate management.',
    initial: 'Service accounts in directory. Some secrets in vault. Manual certificate management.',
    advanced: 'Managed identities for cloud workloads. Automated certificate rotation. Secrets vault integrated.',
    optimal: 'Workload identity federation. Short-lived credentials everywhere. SPIFFE/SPIRE for service mesh.'
  },
  'conditional': {
    description: 'Risk-Based Conditional Access dynamically adjusts access requirements based on real-time risk signals.',
    traditional: 'Static allow/deny rules. No context-aware policies. Access based on network location only.',
    initial: 'Basic conditional access policies. MFA triggered by location. Device compliance required.',
    advanced: 'Risk-based policies using multiple signals. Step-up auth for anomalies. Continuous access evaluation.',
    optimal: 'AI-driven risk scoring. Real-time policy adjustment. Session-level continuous evaluation.'
  },

  // DEVICE PILLAR
  'edr': {
    description: 'Endpoint Detection and Response / Extended Detection and Response monitors endpoints for threats.',
    traditional: 'Legacy antivirus only. Signature-based detection. No centralized visibility.',
    initial: 'Next-gen AV deployed. Basic EDR for servers. Manual threat hunting.',
    advanced: 'EDR on all endpoints. Behavioral detection. Automated response playbooks.',
    optimal: 'XDR correlating across all surfaces. AI-driven threat hunting. Automated containment.'
  },
  'uem': {
    description: 'Unified Endpoint Management / Mobile Device Management ensures all devices are enrolled and compliant.',
    traditional: 'No MDM. Manual device setup. No visibility into device inventory.',
    initial: 'MDM for mobile devices. Basic compliance policies. Corporate vs personal separation.',
    advanced: 'UEM for all device types. Real-time compliance checking. App management.',
    optimal: 'Zero-touch enrollment. Continuous compliance. Dynamic policy based on context.'
  },
  'posture': {
    description: 'Device Posture Assessment verifies device health before granting access.',
    traditional: 'No device health checks. Access granted regardless of patch status.',
    initial: 'Basic health checks at VPN connection. OS version and AV status verified.',
    advanced: 'Continuous posture assessment. Access blocked for non-compliant devices.',
    optimal: 'Real-time posture signals to PDP. Granular access based on device risk score.'
  },
  'cert-id': {
    description: 'Certificate-Based Device Identity cryptographically proves device identity.',
    traditional: 'No device certificates. Identity based on IP or hostname.',
    initial: 'Certificates for domain-joined devices. Manual certificate deployment.',
    advanced: 'PKI for all managed devices. Automated certificate enrollment.',
    optimal: 'Hardware-backed device identity (TPM). Short-lived certificates. Device attestation.'
  },
  'byod': {
    description: 'BYOD Isolation/Containerization protects corporate data on personal devices.',
    traditional: 'No BYOD policy. Personal devices access everything or nothing.',
    initial: 'Basic MDM enrollment for BYOD. App-level access control.',
    advanced: 'Work containers on personal devices. Data isolation enforced.',
    optimal: 'Virtual desktop for sensitive access. Complete work/personal separation.'
  },
  'patch': {
    description: 'Patch & Vulnerability Management ensures systems are updated and vulnerabilities remediated.',
    traditional: 'Manual patching. Monthly or quarterly cycles. No vulnerability scanning.',
    initial: 'Automated patch deployment for OS. Quarterly vulnerability scans.',
    advanced: 'Continuous vulnerability assessment. Risk-based patching priorities.',
    optimal: 'Automated remediation. Zero-day response SLAs. Patch compliance in access decisions.'
  },

  // NETWORK PILLAR
  'ztna': {
    description: 'Zero Trust Network Access replaces VPN with application-level access.',
    traditional: 'Full network VPN. Once connected, broad access to network segments.',
    initial: 'Split-tunnel VPN. Some applications accessed via ZTNA.',
    advanced: 'ZTNA for most remote access. VPN only for legacy. Per-app micro-tunnels.',
    optimal: 'VPN eliminated. All access via ZTNA. Continuous authorization.'
  },
  'microseg': {
    description: 'Microsegmentation limits lateral movement by creating secure zones around workloads.',
    traditional: 'Flat network. VLANs for basic segmentation. East-west traffic unrestricted.',
    initial: 'Host-based firewalls. Basic workload isolation. Manual policy updates.',
    advanced: 'Application-aware microsegmentation. Automated policy discovery.',
    optimal: 'Zero trust between all workloads. Identity-based segmentation. Continuous policy enforcement.'
  },
  'dns': {
    description: 'DNS Security protects against DNS-based attacks and provides visibility.',
    traditional: 'Standard DNS. No logging or filtering. Vulnerable to DNS attacks.',
    initial: 'DNS logging enabled. Basic blocklists for malicious domains.',
    advanced: 'Protective DNS for all traffic. DNS-over-HTTPS. Threat intelligence integration.',
    optimal: 'Real-time DNS threat detection. Automated response. Full DNS visibility.'
  },
  'sdwan': {
    description: 'SD-WAN / SASE provides secure, optimized connectivity for distributed organizations.',
    traditional: 'MPLS-only WAN. Backhauling all traffic to data center.',
    initial: 'Basic SD-WAN for branch. Internet breakout for SaaS.',
    advanced: 'Full SASE with integrated security. Cloud-delivered security stack.',
    optimal: 'Identity-aware routing. Dynamic path selection. Integrated ZT controls.'
  },
  'nta': {
    description: 'Network Traffic Analysis provides visibility into network behavior and anomalies.',
    traditional: 'No network visibility. Basic firewall logs only.',
    initial: 'NetFlow collection. Basic traffic analysis. Manual anomaly detection.',
    advanced: 'ML-based traffic analysis. Encrypted traffic inspection. NDR deployed.',
    optimal: 'Real-time behavioral analysis. Automated threat detection and response.'
  },
  'lateral': {
    description: 'East-West Lateral Movement Controls prevent attackers from moving between systems.',
    traditional: 'No lateral movement controls. Open east-west traffic.',
    initial: 'Network segmentation. Basic firewall rules between zones.',
    advanced: 'Identity-based access between workloads. Microsegmentation deployed.',
    optimal: 'Zero trust between all systems. Real-time lateral movement detection.'
  },

  // APPLICATION PILLAR
  'proxy': {
    description: 'Application Access Proxy provides secure access to applications without network exposure.',
    traditional: 'Applications exposed directly. VPN for remote access.',
    initial: 'Reverse proxy for web apps. Basic authentication at proxy.',
    advanced: 'Cloud-delivered app proxy. Per-app access policies.',
    optimal: 'All apps behind proxy. Continuous authorization. Session-level controls.'
  },
  'api': {
    description: 'API Gateway & Security manages and secures API traffic.',
    traditional: 'Direct API access. No gateway. Basic API keys.',
    initial: 'API gateway deployed. Rate limiting. OAuth for external APIs.',
    advanced: 'Full API lifecycle management. API threat detection. Schema validation.',
    optimal: 'Zero trust API access. Continuous API security testing. Real-time threat blocking.'
  },
  'workload': {
    description: 'Workload/Service Identity provides cryptographic identity for applications and services.',
    traditional: 'No workload identity. Static credentials. IP-based trust.',
    initial: 'Service accounts in directory. Some workloads have managed identity.',
    advanced: 'Cloud workload identity. SPIFFE/SPIRE for on-prem.',
    optimal: 'Unified workload identity. Zero trust between services. Attestation-based trust.'
  },
  'mesh': {
    description: 'Service Mesh provides secure service-to-service communication with mTLS.',
    traditional: 'Direct service communication. No encryption east-west.',
    initial: 'Some services using TLS. Manual certificate management.',
    advanced: 'Service mesh for microservices. Automated mTLS.',
    optimal: 'Full mesh with zero trust policies. Identity-based service access.'
  },
  'dast': {
    description: 'Dynamic/Static Application Security Testing and Runtime Protection identifies vulnerabilities.',
    traditional: 'No security testing. Vulnerabilities found in production.',
    initial: 'Annual penetration tests. Basic SAST in CI/CD.',
    advanced: 'DAST/SAST in pipeline. IAST for critical apps. Vulnerability SLAs.',
    optimal: 'Continuous security testing. RASP deployed. Auto-remediation of findings.'
  },
  'secrets': {
    description: 'Secrets Management securely stores and rotates credentials, keys, and certificates.',
    traditional: 'Secrets in code/config files. Static credentials. No rotation.',
    initial: 'Secrets vault deployed. Manual rotation. Basic access controls.',
    advanced: 'Dynamic secrets. Automated rotation. Secrets scanning in CI/CD.',
    optimal: 'Just-in-time secrets. Zero standing credentials. Full audit trail.'
  },

  // DATA PILLAR
  'classification': {
    description: 'Data Classification categorizes data by sensitivity for appropriate protection.',
    traditional: 'No classification. All data treated equally. No labels.',
    initial: 'Manual classification for some data. Basic taxonomy defined.',
    advanced: 'Automated classification for documents. Labels enforced.',
    optimal: 'AI-driven classification. Real-time labeling. Classification in all systems.'
  },
  'dlp': {
    description: 'Data Loss Prevention prevents unauthorized data exfiltration.',
    traditional: 'No DLP. Data can leave without detection.',
    initial: 'Email DLP. Basic endpoint monitoring. Keyword-based rules.',
    advanced: 'Enterprise DLP across channels. Content inspection. Policy enforcement.',
    optimal: 'Context-aware DLP. AI-based detection. Real-time blocking.'
  },
  'encryption': {
    description: 'Encryption protects data at rest, in transit, and increasingly in use.',
    traditional: 'Minimal encryption. Unencrypted databases. HTTP for internal traffic.',
    initial: 'TLS for external traffic. Full disk encryption. Database TDE.',
    advanced: 'Encryption at rest everywhere. HTTPS only. Key management.',
    optimal: 'Customer-managed keys. Confidential computing. Homomorphic where needed.'
  },
  'access-gov': {
    description: 'Data Access Governance controls who can access what data and when.',
    traditional: 'Wide open permissions. No visibility into data access.',
    initial: 'Basic RBAC for databases. Access reviews for sensitive data.',
    advanced: 'Attribute-based access control. Data access logging.',
    optimal: 'Dynamic data masking. Just-in-time data access. AI-driven policies.'
  },
  'tokenization': {
    description: 'Tokenization/Data Masking replaces sensitive data with non-sensitive tokens.',
    traditional: 'Sensitive data in clear text. No masking in non-prod.',
    initial: 'Basic masking for test environments. PCI tokenization.',
    advanced: 'Format-preserving tokenization. Dynamic masking.',
    optimal: 'Universal tokenization. Real-time de-identification. Differential privacy.'
  },
  'ab869-audit': {
    description: 'AB 869 Audit & Retention Controls ensure compliance with California audit requirements.',
    traditional: 'Minimal logging. No retention policy. Manual audit response.',
    initial: 'Centralized logging. Basic retention policies. Audit on request.',
    advanced: 'Automated audit reports. Compliance dashboards. Alert on violations.',
    optimal: 'Continuous compliance. Real-time audit readiness. Automated evidence collection.'
  },

  // VISIBILITY PILLAR
  'siem': {
    description: 'Security Information and Event Management correlates logs for threat detection.',
    traditional: 'Logs on individual systems. No centralization. Manual review.',
    initial: 'Central log aggregation. Basic correlation rules. Manual triage.',
    advanced: 'SIEM with threat intel. Use case library. Alert tuning.',
    optimal: 'AI-driven detection. Automated correlation. Sub-minute detection times.'
  },
  'ueba': {
    description: 'User and Entity Behavior Analytics detects anomalous behavior patterns.',
    traditional: 'No behavioral baseline. Manual anomaly detection.',
    initial: 'Basic UEBA deployed. Login anomaly detection.',
    advanced: 'Full UEBA coverage. Peer group analysis. Risk scoring.',
    optimal: 'Real-time behavioral analysis. Predictive threat detection. Automated response.'
  },
  'xdr': {
    description: 'Extended Detection and Response correlates signals across security domains.',
    traditional: 'Siloed security tools. Manual correlation.',
    initial: 'Some tool integration. Basic XDR capabilities.',
    advanced: 'Full XDR platform. Automated playbooks. Unified console.',
    optimal: 'AI-driven XDR. Predictive detection. Autonomous response.'
  },
  'soar': {
    description: 'Security Orchestration, Automation and Response automates security operations.',
    traditional: 'Manual incident response. Copy/paste between tools.',
    initial: 'Basic playbooks for common incidents. Some automation.',
    advanced: 'Comprehensive playbook library. Integrated response.',
    optimal: 'Autonomous security operations. Self-healing systems. Minimal human intervention.'
  },
  'asset-inv': {
    description: 'Real-time Asset Inventory provides continuous visibility into all IT assets.',
    traditional: 'Spreadsheet-based inventory. Quarterly updates.',
    initial: 'CMDB with manual updates. Discovery scans.',
    advanced: 'Real-time asset discovery. Cloud integration.',
    optimal: 'Continuous visibility. Unknown asset detection. Auto-classification.'
  },
  'tip': {
    description: 'Threat Intelligence Platform aggregates and operationalizes threat data.',
    traditional: 'No threat intel. Reactive to public disclosures.',
    initial: 'Basic threat feeds. Manual indicator integration.',
    advanced: 'TIP deployed. Automated indicator ingestion. Threat hunting.',
    optimal: 'Real-time threat intel. Predictive analysis. Intelligence-driven defense.'
  },
  'nta': {
    description: 'Network Traffic Analysis provides visibility into network communications and detects anomalies.',
    traditional: 'Basic firewall logs only. No east-west visibility.',
    initial: 'Network monitoring deployed. Basic flow analysis.',
    advanced: 'NDR deployed. ML-based anomaly detection. Protocol analysis.',
    optimal: 'Full network visibility. Real-time threat detection. Automated response.'
  },
  'log-agg': {
    description: 'Log Aggregation provides centralized collection, transformation, and routing of security logs.',
    traditional: 'Logs stored locally on each system. No centralization.',
    initial: 'Basic syslog forwarding. Manual log review.',
    advanced: 'Centralized log pipeline. Data normalization. Retention policies.',
    optimal: 'Real-time log streaming. Intelligent routing. Full audit trail.'
  }
};

// Visibility & Analytics capabilities - cross-cutting function spanning all 5 pillars
export const VISIBILITY_CAPABILITIES = [
  { id: 'siem', name: 'SIEM', vendors: 'Splunk / Microsoft Sentinel', description: 'Security Information and Event Management for centralized log collection and correlation' },
  { id: 'ueba', name: 'UEBA', vendors: 'Exabeam / Securonix', description: 'User and Entity Behavior Analytics for anomaly detection' },
  { id: 'xdr', name: 'XDR Platform', vendors: 'Palo Alto Cortex / CrowdStrike', description: 'Extended Detection and Response across security domains' },
  { id: 'soar', name: 'SOAR', vendors: 'XSOAR / Swimlane', description: 'Security Orchestration, Automation and Response for playbook automation' },
  { id: 'asset-inv', name: 'Asset Inventory', vendors: 'ServiceNow CMDB / Axonius', description: 'Real-time visibility into all IT assets and their security posture' },
  { id: 'tip', name: 'Threat Intel Platform', vendors: 'Recorded Future / Anomali', description: 'Aggregates and operationalizes threat intelligence data' },
  { id: 'nta', name: 'Network Traffic Analysis', vendors: 'Darktrace / Vectra', description: 'Network Detection and Response for east-west traffic visibility' },
  { id: 'log-agg', name: 'Log Aggregation', vendors: 'Elastic / Cribl', description: 'Centralized log pipeline for collection, transformation, and routing' }
];

// Vendor overlay coverage is fetched from the backend via `useVendorOverlay`
// and indexed by `frontend_capability_id` via `buildCoverageMap`. The
// previous hardcoded CISCO_SPLUNK_COVERAGE map was replaced by that flow.

export const PEOPLE_ROLES = [
  { title: 'Executive', desc: 'CIO / CISO / Cyber Risk Officer\nData Governance Lead' },
  { title: 'Architecture', desc: 'Zero Trust Architect\nIdentity + Data + Cloud Security Architects' },
  { title: 'Operations', desc: 'SOC Tier 1-3 | Threat Hunters\nIdentity Ops | Endpoint Team | Net Sec' },
  { title: 'Engineering', desc: 'SOAR / Automation Engineers\nDetection Engineers | Policy Engineers' }
];

export const CORE_PROCESSES = [
  'Identity Lifecycle (Joiner/Mover/Leaver)',
  'Device Trust Enrollment → Compliance → Revocation',
  'Access Decision: Who / What / When / Where / Why',
  'Incident Response (ZT-Aware: Identity ≠ Endpoint)',
  'Data Governance: Classification → Ownership → Audit',
  'Periodic Access Reviews & Privilege Escalation'
];

export const ROADMAP_PHASES = [
  { phase: 1, period: '0–12 months', items: 'MFA Everywhere · Asset Inventory · Initial Data Classification · SIEM Centralization', color: '#eab308' },
  { phase: 2, period: '12–24 months', items: 'ZTNA Replaces VPN · EDR Everywhere · IGA Deployed · Microsegmentation Begins', color: '#3b82f6' },
  { phase: 3, period: '24–36 months', items: 'SOAR Automation · Full Microsegmentation · Data-Centric Controls · PAM Matured', color: '#f97316' },
  { phase: 4, period: '36+ months', items: 'Continuous Trust Evaluation · Policy-Driven Access · Identity + Device + Behavior Fusion', color: '#8b5cf6' }
];

export const MATURITY_LEVELS = ['TRAD.', 'INITIAL', 'ADVANCED', 'OPTIMAL'];

// ============================================================================
// CAPABILITY VENDOR/PRODUCT MAPPINGS
// ============================================================================

export const CAPABILITY_VENDOR_MAPPINGS = {
  // IDENTITY PILLAR
  'idp': {
    vendors: ['Okta', 'Microsoft', 'Google', 'Ping Identity', 'ForgeRock', 'OneLogin', 'Auth0'],
    products: {
      'Okta': ['Okta Identity Cloud', 'Universal Directory', 'Okta SSO'],
      'Microsoft': ['Azure AD', 'Entra ID', 'Azure Active Directory'],
      'Google': ['Google Cloud Identity', 'Google Workspace Identity'],
      'Ping Identity': ['PingOne', 'PingFederate']
    }
  },
  'mfa': {
    vendors: ['Okta', 'Microsoft', 'Duo', 'RSA', 'Google', 'Yubico'],
    products: {
      'Okta': ['Okta Adaptive MFA', 'Okta Verify'],
      'Microsoft': ['Azure MFA', 'Microsoft Authenticator'],
      'Duo': ['Duo Security', 'Duo MFA', 'Duo Access'],
      'RSA': ['RSA SecurID', 'RSA Authentication Manager'],
      'Yubico': ['YubiKey', 'Yubico OTP']
    }
  },
  'iga': {
    vendors: ['SailPoint', 'Saviynt', 'Microsoft', 'One Identity', 'Omada', 'IBM'],
    products: {
      'SailPoint': ['IdentityNow', 'IdentityIQ', 'Identity Platform'],
      'Saviynt': ['Enterprise Identity Cloud', 'IGA'],
      'Microsoft': ['Entra ID Governance', 'Azure AD Identity Governance', 'Access Reviews'],
      'One Identity': ['Identity Manager', 'One Identity'],
      'IBM': ['Security Identity Governance']
    }
  },
  'pam': {
    vendors: ['CyberArk', 'BeyondTrust', 'Delinea', 'HashiCorp', 'Centrify'],
    products: {
      'CyberArk': ['Privileged Access Manager', 'CyberArk PAM', 'Privilege Cloud'],
      'BeyondTrust': ['Privilege Management', 'Password Safe'],
      'Delinea': ['Secret Server', 'Privilege Manager'],
      'HashiCorp': ['Vault', 'Boundary']
    }
  },
  'machine-id': {
    vendors: ['HashiCorp', 'Venafi', 'AppViewX', 'Keyfactor', 'CyberArk'],
    products: {
      'HashiCorp': ['Vault', 'Consul'],
      'Venafi': ['Trust Protection Platform', 'Venafi as a Service'],
      'CyberArk': ['Secrets Manager', 'Conjur']
    }
  },
  'conditional': {
    vendors: ['Microsoft', 'Okta', 'Google', 'Cisco', 'Zscaler'],
    products: {
      'Microsoft': ['Conditional Access', 'Azure AD Conditional Access'],
      'Okta': ['Okta Adaptive MFA', 'Okta FastPass'],
      'Google': ['Context-Aware Access', 'BeyondCorp']
    }
  },
  // DEVICE PILLAR
  'edr': {
    vendors: ['CrowdStrike', 'SentinelOne', 'Microsoft', 'Carbon Black', 'Palo Alto', 'Sophos'],
    products: {
      'CrowdStrike': ['Falcon', 'Falcon Insight', 'Falcon XDR'],
      'SentinelOne': ['Singularity', 'Singularity XDR'],
      'Microsoft': ['Defender for Endpoint', 'Microsoft Defender XDR'],
      'Carbon Black': ['Carbon Black Cloud', 'CB Defense'],
      'Palo Alto': ['Cortex XDR']
    }
  },
  'uem': {
    vendors: ['Microsoft', 'VMware', 'Jamf', 'Ivanti', 'Google', 'IBM'],
    products: {
      'Microsoft': ['Intune', 'Microsoft Endpoint Manager', 'SCCM'],
      'VMware': ['Workspace ONE', 'AirWatch'],
      'Jamf': ['Jamf Pro', 'Jamf Now'],
      'Google': ['Google Endpoint Management', 'Chrome Enterprise'],
      'Ivanti': ['Neurons for UEM', 'Endpoint Manager']
    }
  },
  'posture': {
    vendors: ['CrowdStrike', 'Microsoft', 'Tanium', 'Qualys', 'Rapid7'],
    products: {
      'CrowdStrike': ['Falcon Zero Trust Assessment', 'Falcon Spotlight'],
      'Microsoft': ['Defender for Endpoint', 'Intune Compliance'],
      'Tanium': ['Tanium Comply', 'Tanium Asset']
    }
  },
  'cert-id': {
    vendors: ['Venafi', 'DigiCert', 'Entrust', 'Microsoft', 'Sectigo'],
    products: {
      'Venafi': ['Trust Protection Platform'],
      'DigiCert': ['CertCentral', 'DigiCert ONE'],
      'Microsoft': ['Active Directory Certificate Services', 'Azure PKI']
    }
  },
  'byod': {
    vendors: ['VMware', 'Microsoft', 'MobileIron', 'Citrix', 'BlackBerry'],
    products: {
      'VMware': ['Workspace ONE', 'AirWatch Container'],
      'Microsoft': ['Intune MAM', 'App Protection Policies'],
      'Citrix': ['Secure Hub', 'Workspace']
    }
  },
  'patch': {
    vendors: ['Microsoft', 'Tanium', 'Ivanti', 'Qualys', 'Rapid7', 'Automox'],
    products: {
      'Microsoft': ['WSUS', 'Intune', 'SCCM'],
      'Tanium': ['Tanium Patch', 'Tanium Deploy'],
      'Qualys': ['Qualys Patch Management', 'VMDR'],
      'Automox': ['Automox Patch']
    }
  },
  // NETWORK PILLAR
  'ztna': {
    vendors: ['Zscaler', 'Palo Alto', 'Cloudflare', 'Netskope', 'Cisco', 'Akamai'],
    products: {
      'Zscaler': ['Zscaler Private Access', 'ZPA'],
      'Palo Alto': ['Prisma Access', 'GlobalProtect'],
      'Cloudflare': ['Cloudflare Access', 'Cloudflare Zero Trust'],
      'Netskope': ['Netskope Private Access'],
      'Cisco': ['Duo Network Gateway', 'Cisco Secure Access']
    }
  },
  'microseg': {
    vendors: ['Illumio', 'Guardicore', 'VMware', 'Akamai', 'Cisco'],
    products: {
      'Illumio': ['Illumio Core', 'Illumio Edge'],
      'Guardicore': ['Guardicore Centra', 'Akamai Guardicore'],
      'VMware': ['NSX', 'NSX-T']
    }
  },
  'dns': {
    vendors: ['Cisco', 'Infoblox', 'Cloudflare', 'Akamai', 'Palo Alto'],
    products: {
      'Cisco': ['Umbrella', 'Cisco Umbrella'],
      'Cloudflare': ['Cloudflare Gateway', '1.1.1.1 for Teams'],
      'Infoblox': ['BloxOne Threat Defense']
    }
  },
  'sdwan': {
    vendors: ['VMware', 'Cisco', 'Fortinet', 'Palo Alto', 'Zscaler', 'Cato Networks'],
    products: {
      'VMware': ['VMware SD-WAN', 'VeloCloud'],
      'Cisco': ['Cisco SD-WAN', 'Meraki SD-WAN'],
      'Fortinet': ['FortiSASE', 'Fortinet SD-WAN'],
      'Zscaler': ['Zero Trust Exchange']
    }
  },
  'nta': {
    vendors: ['Darktrace', 'Vectra', 'Cisco', 'ExtraHop', 'Corelight'],
    products: {
      'Darktrace': ['Enterprise', 'Darktrace DETECT'],
      'Vectra': ['Vectra AI', 'Cognito'],
      'Cisco': ['Stealthwatch', 'Secure Network Analytics']
    }
  },
  'lateral': {
    vendors: ['Illumio', 'Guardicore', 'VMware', 'CrowdStrike', 'Microsoft'],
    products: {
      'Illumio': ['Illumio Core'],
      'CrowdStrike': ['Falcon Identity Protection'],
      'Microsoft': ['Defender for Identity']
    }
  },
  // APPLICATION PILLAR
  'proxy': {
    vendors: ['Cloudflare', 'Akamai', 'Netskope', 'Zscaler', 'F5'],
    products: {
      'Cloudflare': ['Cloudflare Access', 'Cloudflare Gateway'],
      'Akamai': ['Enterprise Application Access', 'EAA'],
      'Netskope': ['Netskope Private Access'],
      'F5': ['BIG-IP APM', 'Access Policy Manager']
    }
  },
  'api': {
    vendors: ['Apigee', 'Kong', 'AWS', 'Azure', 'MuleSoft', 'Salt Security'],
    products: {
      'Apigee': ['Apigee Edge', 'Apigee X'],
      'Kong': ['Kong Gateway', 'Kong Enterprise'],
      'AWS': ['API Gateway', 'AWS API Gateway'],
      'Azure': ['Azure API Management']
    }
  },
  'workload': {
    vendors: ['HashiCorp', 'Aqua', 'Palo Alto', 'CrowdStrike', 'Wiz'],
    products: {
      'HashiCorp': ['Vault', 'Consul', 'Nomad'],
      'Aqua': ['Aqua Platform', 'Aqua Security'],
      'Palo Alto': ['Prisma Cloud']
    }
  },
  'mesh': {
    vendors: ['Istio', 'Linkerd', 'Consul', 'AWS', 'Kong'],
    products: {
      'Istio': ['Istio Service Mesh'],
      'Linkerd': ['Linkerd'],
      'Consul': ['HashiCorp Consul', 'Consul Connect'],
      'AWS': ['App Mesh']
    }
  },
  'dast': {
    vendors: ['Veracode', 'Checkmarx', 'Snyk', 'HCL', 'Synopsys', 'Rapid7'],
    products: {
      'Veracode': ['Veracode DAST', 'Dynamic Analysis'],
      'Checkmarx': ['Checkmarx DAST', 'CxDAST'],
      'Synopsys': ['WhiteHat', 'Synopsys DAST'],
      'HCL': ['AppScan']
    }
  },
  'secrets': {
    vendors: ['HashiCorp', 'CyberArk', 'AWS', 'Azure', 'Google'],
    products: {
      'HashiCorp': ['Vault', 'Vault Enterprise'],
      'CyberArk': ['Conjur', 'Secrets Manager'],
      'AWS': ['Secrets Manager', 'AWS Secrets Manager'],
      'Azure': ['Key Vault', 'Azure Key Vault'],
      'Google': ['Secret Manager', 'Cloud KMS']
    }
  },
  // DATA PILLAR
  'classification': {
    vendors: ['Microsoft', 'Google', 'Varonis', 'BigID', 'Spirion'],
    products: {
      'Microsoft': ['Microsoft Purview', 'Azure Information Protection', 'Sensitivity Labels'],
      'Google': ['Cloud DLP', 'Sensitive Data Protection'],
      'Varonis': ['Data Classification Engine', 'DatAdvantage']
    }
  },
  'dlp': {
    vendors: ['Microsoft', 'Symantec', 'Forcepoint', 'Digital Guardian', 'Netskope', 'Zscaler'],
    products: {
      'Microsoft': ['Microsoft Purview DLP', 'Defender for Cloud Apps'],
      'Symantec': ['Symantec DLP', 'Data Loss Prevention'],
      'Forcepoint': ['Forcepoint DLP'],
      'Netskope': ['Netskope DLP']
    }
  },
  'encryption': {
    vendors: ['Microsoft', 'Google', 'AWS', 'Thales', 'Vormetric'],
    products: {
      'Microsoft': ['Azure Encryption', 'BitLocker', 'Azure Disk Encryption'],
      'AWS': ['AWS KMS', 'CloudHSM'],
      'Thales': ['CipherTrust', 'Vormetric']
    }
  },
  'access-gov': {
    vendors: ['Varonis', 'SailPoint', 'Saviynt', 'One Identity', 'Microsoft'],
    products: {
      'Varonis': ['DatAdvantage', 'DataPrivilege'],
      'SailPoint': ['IdentityNow', 'File Access Manager'],
      'Microsoft': ['Entra Permissions Management']
    }
  },
  'tokenization': {
    vendors: ['Protegrity', 'Voltage', 'TokenEx', 'Thales', 'Informatica'],
    products: {
      'Protegrity': ['Data Protection Platform'],
      'Voltage': ['SecureData', 'Voltage SecureData'],
      'Thales': ['CipherTrust Tokenization']
    }
  },
  'ab869-audit': {
    vendors: ['Splunk', 'Microsoft', 'Elastic', 'Sumo Logic', 'LogRhythm'],
    products: {
      'Splunk': ['Splunk Enterprise', 'Splunk Cloud'],
      'Microsoft': ['Microsoft Sentinel', 'Azure Monitor'],
      'Elastic': ['Elastic Security', 'Elasticsearch']
    }
  },
  // VISIBILITY PILLAR
  'siem': {
    vendors: ['Splunk', 'Microsoft', 'Google', 'IBM', 'Elastic', 'LogRhythm', 'Sumo Logic'],
    products: {
      'Splunk': ['Splunk Enterprise Security', 'Splunk SIEM'],
      'Microsoft': ['Microsoft Sentinel', 'Azure Sentinel'],
      'Google': ['Chronicle SIEM', 'Google Security Operations'],
      'IBM': ['QRadar', 'IBM Security QRadar']
    }
  },
  'ueba': {
    vendors: ['Exabeam', 'Securonix', 'Microsoft', 'Splunk', 'Gurucul'],
    products: {
      'Exabeam': ['Exabeam Fusion', 'Advanced Analytics'],
      'Securonix': ['Securonix UEBA', 'Security Analytics'],
      'Microsoft': ['Sentinel UEBA', 'Defender for Identity']
    }
  },
  'xdr': {
    vendors: ['Palo Alto', 'CrowdStrike', 'Microsoft', 'SentinelOne', 'Trend Micro'],
    products: {
      'Palo Alto': ['Cortex XDR', 'Cortex XSIAM'],
      'CrowdStrike': ['Falcon XDR', 'Falcon Platform'],
      'Microsoft': ['Microsoft Defender XDR', 'Microsoft 365 Defender'],
      'SentinelOne': ['Singularity XDR']
    }
  },
  'soar': {
    vendors: ['Palo Alto', 'Splunk', 'Swimlane', 'Tines', 'ServiceNow'],
    products: {
      'Palo Alto': ['Cortex XSOAR', 'XSOAR'],
      'Splunk': ['Splunk SOAR', 'Phantom'],
      'Swimlane': ['Swimlane SOAR']
    }
  },
  'asset-inv': {
    vendors: ['ServiceNow', 'Axonius', 'Tanium', 'Qualys', 'Rapid7'],
    products: {
      'ServiceNow': ['CMDB', 'IT Asset Management'],
      'Axonius': ['Axonius Asset Management', 'Cybersecurity Asset Management'],
      'Tanium': ['Tanium Asset', 'Asset Inventory']
    }
  },
  'tip': {
    vendors: ['Recorded Future', 'Anomali', 'ThreatConnect', 'MITRE', 'Mandiant'],
    products: {
      'Recorded Future': ['Recorded Future Intelligence'],
      'Anomali': ['ThreatStream', 'Anomali Platform'],
      'ThreatConnect': ['ThreatConnect Platform'],
      'Mandiant': ['Mandiant Advantage']
    }
  },
  'nta': {
    vendors: ['Darktrace', 'Vectra', 'Cisco', 'ExtraHop', 'Corelight'],
    products: {
      'Darktrace': ['Darktrace DETECT', 'Darktrace RESPOND'],
      'Vectra': ['Vectra NDR', 'Vectra AI'],
      'Cisco': ['Cisco Secure Network Analytics', 'Stealthwatch'],
      'ExtraHop': ['Reveal(x)', 'ExtraHop NDR']
    }
  },
  'log-agg': {
    vendors: ['Elastic', 'Cribl', 'Splunk', 'Datadog', 'Sumo Logic'],
    products: {
      'Elastic': ['Elasticsearch', 'Elastic Agent', 'Logstash'],
      'Cribl': ['Cribl Stream', 'Cribl Edge'],
      'Splunk': ['Splunk Universal Forwarder', 'Splunk Cloud'],
      'Datadog': ['Datadog Log Management']
    }
  }
};
